import type { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import type { ParsedQuery } from "query-string";
import { shipmentFilterEmptyDescription } from "@/Shared/ShipmentFilter/Helpers/shipmentFilterEmptyDescription";
import { SortShipmentOptions } from "@/Shared/Shipment/SortShipmentOptions";

// Query params parse as string | array | null. The filter only accepts a
// single non-empty string, so anything else is treated as absent.
function stringQueryValue(
  value: string | (string | null)[] | null | undefined,
): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

function isSortShipmentOption(value: string): value is SortShipmentOptions {
  const options: readonly string[] = Object.values(SortShipmentOptions);
  return options.includes(value);
}

export function viewShipmentsFilterDescriptionFromQueryParams(
  query: ParsedQuery,
) {
  const filter = shipmentFilterEmptyDescription();
  let sort: SortShipmentOptions = SortShipmentOptions.PickupDate;

  filter.proNumber = stringQueryValue(query["proNumber"]);
  filter.bolNumber = stringQueryValue(query["bolNumber"]);
  filter.carrierIdentifier = stringQueryValue(query["carrierIdentifier"]);

  const minPriceDollars = stringQueryValue(query["minPriceDollars"]);
  if (minPriceDollars !== undefined) {
    filter.minPriceDollars = parseFloat(minPriceDollars);
  }

  const maxPriceDollars = stringQueryValue(query["maxPriceDollars"]);
  if (maxPriceDollars !== undefined) {
    filter.maxPriceDollars = parseFloat(maxPriceDollars);
  }

  if (query["shipmentState"]) {
    filter.shipmentState = query["shipmentState"] as ShipmentState;
  }

  filter.pickupReferenceNumber = stringQueryValue(
    query["pickupReferenceNumber"],
  );
  filter.pickupBusinessName = stringQueryValue(query["pickupBusinessName"]);
  filter.pickupPostalCode = stringQueryValue(query["pickupPostalCode"]);
  filter.pickupStateOrProvinceCode = stringQueryValue(
    query["pickupStateOrProvinceCode"],
  );
  filter.deliveryReferenceNumber = stringQueryValue(
    query["deliveryReferenceNumber"],
  );
  filter.deliveryBusinessName = stringQueryValue(query["deliveryBusinessName"]);
  filter.deliveryPostalCode = stringQueryValue(query["deliveryPostalCode"]);
  filter.deliveryStateOrProvinceCode = stringQueryValue(
    query["deliveryStateOrProvinceCode"],
  );
  filter.bookedAtStartRange = stringQueryValue(query["bookedAtStartRange"]);
  filter.bookedAtEndRange = stringQueryValue(query["bookedAtEndRange"]);
  filter.pickupDateStartRange = stringQueryValue(query["pickupDateStartRange"]);
  filter.pickupDateEndRange = stringQueryValue(query["pickupDateEndRange"]);
  filter.deliveredAtStartRange = stringQueryValue(
    query["deliveredAtStartRange"],
  );
  filter.deliveredAtEndRange = stringQueryValue(query["deliveredAtEndRange"]);

  const sortParam = stringQueryValue(query["sort"]);
  if (sortParam !== undefined && isSortShipmentOption(sortParam)) {
    sort = sortParam;
  }

  if (query["onlyShowFreightClaims"]) {
    filter.onlyShowFreightClaims = query["onlyShowFreightClaims"] === "true";
  }

  return { filter, sort };
}
